import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../../lib/api";
import { routingPlan } from "../../lib/vpa";

const INK = "#14101F";
const VIOLET = "#4931E8";
const LIME = "#C7F23D";
const MINT = "#1FBF75";

function inr(n) {
  if (n == null) return "₹0";
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 2)}L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}
function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const sec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}
// Prefer the captured payer name, then the VPA, never a bare "unknown@upi".
function payerLabel(t) {
  const n = (t.customer_name || "").trim();
  if (n) return n;
  const v = (t.payer_vpa || "").trim();
  if (v && v !== "unknown@upi") return v;
  const p = (t.customer_phone || "").trim();
  return p || "Payment received";
}

export default function Overview() {
  const { upiIds, merchant, routing } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  // Where money lands: the active routing head (not the is_default flag, which
  // may point at a disabled VPA). Several active VPAs -> say so, not one of them.
  const plan = routingPlan(upiIds, merchant, routing);
  const activeCount = plan.p2m.active + plan.p2p.active;
  const settledInto = activeCount > 1 ? `your ${activeCount} UPI IDs` : plan.p2m.next || plan.p2p.next || "your own UPI";

  useEffect(() => {
    api.get("/overview/stats").then(({ data }) => setStats(data));
    // page_size (not limit): `limit` is the legacy bare-array contract kept for the mobile app
    api.get("/transactions?page_size=4").then(({ data }) => setRecent(data.items || []));
  }, []);

  // Build a contiguous 7-day window ending today, aligned to the backend's
  // per-UTC-day buckets, filling days with no settlements as 0. This keeps the
  // bar labels honest (real weekdays) instead of a static Mon–Sun row.
  const dayMap = Object.fromEntries((stats?.week || []).map((w) => [w.day, w.sum || 0]));
  const series = [];
  for (let k = 6; k >= 0; k--) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - k);
    const key = d.toISOString().substring(0, 10);
    series.push({
      key,
      label: d.toLocaleDateString("en-IN", { weekday: "short" }),
      sum: dayMap[key] || 0,
      isToday: k === 0,
    });
  }
  const maxWeek = Math.max(1, ...series.map((s) => s.sum));
  const weekTotal = series.reduce((s, x) => s + x.sum, 0);

  return (
    <div className="ui-page" data-testid="overview-page">
      <div className="ui-grid ui-grid-4">
        <Stat label="Collected today" value={inr(stats?.today?.sum || 0)} sub={`${stats?.today?.count || 0} payments`} dataTestid="stat-today" />
        <Stat label="This month" value={inr(stats?.month?.sum || 0)} sub={`${stats?.month?.count || 0} payments`} dataTestid="stat-month" />
        <Stat label="Success rate" value={`${stats?.success_rate ?? 100}%`} sub={`${stats?.captured_total || 0} payments captured`} bg={VIOLET} fg="#fff" labelColor={LIME} subColor="#d8d2ff" dataTestid="stat-success" />
        <Stat label="Active links" value={stats?.active_links ?? 0} sub="payment links live" dataTestid="stat-links" />
      </div>

      <div className="ov-row">
        <section className="ui-card" data-testid="weekly-chart">
          <header className="ui-card-head">
            <div style={{ minWidth: 0 }}>
              <h3 className="ui-card-title">Collections this week</h3>
              <p className="ui-card-sub">Last 7 days · today highlighted</p>
            </div>
            <span className="ui-badge" style={{ color: INK, background: "#ECE8FB" }}>{inr(weekTotal)} total</span>
          </header>
          <div className="ui-card-pad">
            <div className="ov-bars">
              {series.map((row) => {
                const pct = row.sum > 0 ? Math.max(8, Math.round((row.sum / maxWeek) * 100)) : 4;
                return (
                  <div key={row.key} className="ov-bar-col" title={`${row.label}: ${inr(row.sum)}`}>
                    <div className="ov-bar-track">
                      <div style={{ width: "100%", height: `${pct}%`, background: row.isToday ? VIOLET : row.sum >= maxWeek && maxWeek > 0 ? LIME : "#ECE8FB", border: `2px solid ${INK}`, borderRadius: "8px 8px 0 0", transition: "height .3s" }} />
                    </div>
                    <span className="ui-mono" style={{ fontSize: 10.5, color: row.isToday ? INK : "#6a6478", fontWeight: row.isToday ? 700 : 400 }}>{row.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="ui-card" style={{ display: "flex", flexDirection: "column" }} data-testid="recent-deposits">
          <header className="ui-card-head">
            <h3 className="ui-card-title">Recent deposits</h3>
            <a href="/deposits" className="ui-btn ui-btn-ghost ui-btn-sm ui-mono" style={{ color: VIOLET, fontSize: 11, textTransform: "uppercase", minHeight: 36 }}>View all →</a>
          </header>
          <div className="ui-card-pad" style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            {recent.length === 0 && <div className="ui-muted" style={{ fontSize: 13, padding: "8px 0" }}>No deposits yet — share a payment link to start collecting.</div>}
            {recent.map((t) => (
              <div key={t.id} className="ov-dep" data-testid={`deposit-row-${t.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <span className="ov-dep-icon">↓</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="ui-trunc" style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, fontWeight: 700 }}>{payerLabel(t)}</div>
                    <div className="ui-mono ui-faint" style={{ fontSize: 10.5 }}>{timeAgo(t.created_at)}</div>
                  </div>
                </div>
                <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 15, flex: "none" }}>{inr(t.amount)}</div>
              </div>
            ))}
            <div style={{ marginTop: "auto", paddingTop: 14 }}>
              <div className="ui-banner is-info" style={{ fontSize: 12.5, lineHeight: 1.45, padding: "10px 13px", display: "block", wordBreak: "break-word" }}>
                <strong>⚡ Settled instantly</strong> into {settledInto} — SuperCash never held a rupee.
              </div>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .ov-row { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: 14px; align-items: stretch; }
        .ov-bars { display: flex; align-items: stretch; gap: 10px; height: 180px; }
        .ov-bar-col { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 7px; }
        .ov-bar-track { flex: 1; width: 100%; display: flex; align-items: flex-end; }
        .ov-dep { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--ui-line-soft); min-height: 48px; }
        .ov-dep:last-of-type { border-bottom: none; }
        .ov-dep-icon { width: 32px; height: 32px; flex: none; border-radius: 9px; background: #DFF7E7; border: 2px solid ${INK}; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; }
        .ov-stat-value { font-family: "Inter", sans-serif; font-weight: 800; font-size: 28px; letter-spacing: -0.02em; line-height: 1.1; margin: 4px 0 2px; overflow-wrap: anywhere; }
        @media (max-width: 900px) { .ov-row { grid-template-columns: minmax(0, 1fr); } }
        @media (max-width: 480px) {
          .ov-bars { gap: 6px; height: 150px; }
          .ov-stat-value { font-size: 20px; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, sub, bg, fg = INK, labelColor, subColor = MINT, dataTestid }) {
  return (
    <div className="ui-card ui-stat" style={bg ? { background: bg, color: fg } : undefined} data-testid={dataTestid}>
      <span className="ui-eyebrow" style={labelColor ? { color: labelColor } : undefined}>{label}</span>
      <span className="ov-stat-value">{value}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: subColor }}>{sub}</span>
    </div>
  );
}
